import React from "react";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import Summary, { SummaryText, Actions } from "../Summary";

/**
 * "Letter to your future self" — the payoff of the course.
 *
 * Every blank arrives pre-composed from six earlier challenges (see
 * Interactive::DTLYL::Letter#prepare) and stays editable, so the learner reads
 * their own work assembled into prose and can adjust the wording.
 *
 * Copy transcribed from the Ember template it replaces.
 */

interface FieldProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * An editable blank that sits inline in a sentence. contentEditable rather than
 * an <input> so it flows with the surrounding text and grows with its content.
 */
const LetterField: React.FC<FieldProps> = ({
  value,
  placeholder,
  onChange,
  disabled,
}) => {
  if (disabled) return <span className="accent">{value}</span>;

  return (
    <span
      className="accent dtlyl-letter-field"
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      // Uncontrolled on purpose: re-rendering a contentEditable on every
      // keystroke moves the caret to the start.
      onBlur={(e) => onChange(e.currentTarget.textContent || "")}
    >
      {value}
    </span>
  );
};

const Letter: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const firstName =
    currentUser?.first_name || (currentUser?.name || "").split(" ")[0] || "you";

  const field = (key: string, placeholder?: string) => (
    <LetterField
      value={payload[key] || ""}
      placeholder={placeholder}
      disabled={finished}
      onChange={(value) => update({ ...payload, [key]: value })}
    />
  );

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser}>
        <SummaryText>
          Your letter is written. You’ll find it in your life map at the end of
          the course.
        </SummaryText>
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} />

      <div className="dtlyl-letter">
        <p>Dear {firstName}</p>

        <p>
          I’ve been doing some thinking. In fact, I deconstructed my life,
          shifted my point of view, and then reconstructed my life to design the
          life I want to live. This is why I’m writing this letter - this is my
          expression of the life I love.
        </p>

        <p>
          When I deconstructed my life I realised I have too much{" "}
          {field("too_much")}, and not enough {field("too_little")}.
        </p>

        <p>
          My {payload.hero_salutation || "hero"} reminded me of my own values,{" "}
          {field("values", "…")}.
        </p>

        <p>
          A metaphor that represents my life today is{" "}
          {field("metaphor_today", "…")}, however I am redesigning it to be more
          like {field("metaphor_tomorrow", "…")} in the future. To help me
          transform my life today into the life I want in the future, I’m going
          to {field("from_today_to_tomorrow_strategies", "…")}.
        </p>

        <p>
          This led me to reconstruct my life. The things that really matter to me
          that I want to include in my life are{" "}
          {field("quadrants_reconstruction_priorities", "…")}.
        </p>

        <p>This is the life I love, the life I hope you’re proudly living.</p>

        <p>
          One small thing that I’m going to start doing every day in order to
          reach my dream is {field("expression_task", "…")} because, as Aristotle
          said, “you are what you repeatedly do”.
        </p>

        <p>
          Realising all of this and making these changes in my life is important
          to me because {field("motivation", "explain your motivations")}.
        </p>

        <p>You can do this, future self, I believe in you!</p>

        <p>Love, {firstName}.</p>

        <p>P.S. {field("post_script", "say anything you want")}</p>
      </div>

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={submitting}
          loading={submitting}
          onClick={submit}
        >
          Submit
        </Button>
      </Actions>
    </div>
  );
};

export default Letter;
